/**
 * image-zoom.js — Fullscreen image zoom modal (extends Dialog)
 *
 * Global delegated listener on document for any element tagged
 * `data-zoom-trigger` (usually product gallery images). Opens a fullscreen
 * modal with:
 *   • pinch-to-zoom on touch (two-finger gesture)
 *   • wheel/button zoom on desktop
 *   • drag-pan when zoomed
 *   • keyboard arrows to switch images in the active gallery
 *   • reset + close buttons
 *
 * The modal markup lives in `snippets/image-zoom.liquid` and is rendered
 * once at the bottom of `layout/theme.liquid`.
 *
 * Sibling element discovery:
 *   When a trigger is clicked, we look for its closest parent that is also
 *   a common image container (.product-gallery, [data-zoom-gallery]) and
 *   collect ALL [data-zoom-trigger] images inside as the "gallery" — that
 *   drives the prev/next navigation.
 *
 * Data attributes on triggers:
 *   data-zoom-trigger         — enables the listener (required)
 *   data-zoom-src             — hi-res src (optional, falls back to img src)
 *   data-zoom-alt             — alt text (optional, falls back to img alt)
 *
 * Import name: @theme/image-zoom
 */

import { Dialog } from '@theme/dialog';

const MIN_ZOOM = 1;
const MAX_ZOOM = 5;
const ZOOM_STEP = 0.5;

class ImageZoom extends Dialog {
  /** @type {HTMLImageElement | null} */ #img = null;
  /** @type {HTMLElement | null} */      #stage = null;
  /** @type {HTMLElement | null} */      #levelLabel = null;

  /** @type {Array<{src:string, alt:string, el:HTMLElement}>} */ #gallery = [];
  /** @type {number} */ #index = 0;

  /** @type {number} */  #scale = 1;
  /** @type {number} */  #tx = 0;
  /** @type {number} */  #ty = 0;

  /** @type {boolean} */ #isPanning = false;
  /** @type {number} */  #panStartX = 0;
  /** @type {number} */  #panStartY = 0;
  /** @type {number} */  #panStartTx = 0;
  /** @type {number} */  #panStartTy = 0;

  /** @type {number | null} */ #pinchStartDist = null;
  /** @type {number} */        #pinchStartScale = 1;
  /** @type {Map<number,PointerEvent>} */ #pointers = new Map();

  onDialogConnect() {
    this.#img = this.querySelector('.image-zoom__image');
    this.#stage = this.querySelector('.image-zoom__stage');
    this.#levelLabel = this.querySelector('[data-zoom-level]');

    // Global delegated click — any [data-zoom-trigger] opens the modal.
    this.on(document, 'click', (e) => {
      const target = e.target instanceof Element ? e.target : null;
      if (!target) return;
      const trigger = target.closest('[data-zoom-trigger]');
      if (!trigger) return;
      e.preventDefault();
      this.#openForTrigger(trigger);
    });

    // Zoom buttons
    this.querySelectorAll('[data-zoom-action]').forEach((btn) => {
      this.on(btn, 'click', (e) => {
        e.preventDefault();
        const action = /** @type {HTMLElement} */ (e.currentTarget).dataset.zoomAction;
        if (action === 'in') this.#zoomBy(ZOOM_STEP);
        else if (action === 'out') this.#zoomBy(-ZOOM_STEP);
        else if (action === 'reset') this.#resetTransform();
      });
    });

    // Prev/next
    const prev = this.querySelector('.image-zoom__prev');
    const next = this.querySelector('.image-zoom__next');
    if (prev) this.on(prev, 'click', (e) => { e.preventDefault(); this.#navigate(-1); });
    if (next) this.on(next, 'click', (e) => { e.preventDefault(); this.#navigate(1); });

    // Keyboard: arrows navigate, +/- zoom, 0 reset
    this.on(document, 'keydown', (e) => {
      if (!this.isOpen) return;
      if (e.key === 'ArrowLeft')  { e.preventDefault(); this.#navigate(-1); }
      else if (e.key === 'ArrowRight') { e.preventDefault(); this.#navigate(1); }
      else if (e.key === '+' || e.key === '=') { e.preventDefault(); this.#zoomBy(ZOOM_STEP); }
      else if (e.key === '-' || e.key === '_') { e.preventDefault(); this.#zoomBy(-ZOOM_STEP); }
      else if (e.key === '0') { e.preventDefault(); this.#resetTransform(); }
    });

    // Pointer interactions on the stage.
    // Native touch gestures (pinch-zoom, scroll) are disabled via CSS
    // `touch-action: none` on .image-zoom__stage, so no touchmove listener
    // is needed here — we rely entirely on unified Pointer Events.
    if (this.#stage) {
      this.on(this.#stage, 'pointerdown', this.#onPointerDown);
      this.on(this.#stage, 'pointermove', this.#onPointerMove);
      this.on(this.#stage, 'pointerup', this.#onPointerUp);
      this.on(this.#stage, 'pointercancel', this.#onPointerUp);
      this.on(this.#stage, 'pointerleave', this.#onPointerUp);
      this.on(this.#stage, 'wheel', this.#onWheel, { passive: false });
    }
  }

  onClose() {
    this.#resetTransform();
    this.#pointers.clear();
  }

  /**
   * @param {Element} trigger
   */
  #openForTrigger(trigger) {
    // Discover gallery from closest .product-gallery or [data-zoom-gallery]
    const container =
      trigger.closest('[data-zoom-gallery]') ||
      trigger.closest('.product-gallery') ||
      trigger.closest('section') ||
      document.body;
    const items = Array.from(container.querySelectorAll('[data-zoom-trigger]'));

    this.#gallery = items.map((el) => {
      const img = el instanceof HTMLImageElement ? el : el.querySelector('img');
      return {
        src: (el instanceof HTMLElement && el.dataset.zoomSrc) || (img && img.currentSrc) || (img && img.src) || '',
        alt: (el instanceof HTMLElement && el.dataset.zoomAlt) || (img && img.alt) || '',
        el: /** @type {HTMLElement} */ (el),
      };
    }).filter((g) => g.src);

    // Index of the clicked trigger
    this.#index = Math.max(0, items.indexOf(/** @type {Element} */ (trigger)));
    if (this.#gallery.length === 0) return;

    this.#loadCurrent();
    // Update prev/next visibility
    const prev = this.querySelector('.image-zoom__prev');
    const next = this.querySelector('.image-zoom__next');
    const multi = this.#gallery.length > 1;
    if (prev instanceof HTMLElement) prev.hidden = !multi;
    if (next instanceof HTMLElement) next.hidden = !multi;

    this.open(trigger instanceof HTMLElement ? trigger : null);
  }

  #loadCurrent() {
    if (!this.#img || !this.#gallery[this.#index]) return;
    const item = this.#gallery[this.#index];
    // Build a high-res Shopify URL if possible (strip _nnnx query)
    let hiRes = item.src;
    try {
      const u = new URL(hiRes, window.location.href);
      // Shopify CDN: swap width param to a high value
      if (u.searchParams.has('width')) {
        u.searchParams.set('width', '2000');
        hiRes = u.toString();
      }
    } catch (_) { /* noop */ }
    this.#img.src = hiRes;
    this.#img.alt = item.alt || '';
    this.#resetTransform();
  }

  /**
   * @param {number} delta
   */
  #navigate(delta) {
    if (this.#gallery.length === 0) return;
    const len = this.#gallery.length;
    this.#index = (this.#index + delta + len) % len;
    this.#loadCurrent();
  }

  /**
   * @param {number} delta
   */
  #zoomBy(delta) {
    this.#setScale(this.#scale + delta);
  }

  /**
   * @param {number} next
   */
  #setScale(next) {
    const clamped = Math.max(MIN_ZOOM, Math.min(MAX_ZOOM, next));
    this.#scale = clamped;
    if (clamped === 1) {
      this.#tx = 0;
      this.#ty = 0;
    }
    this.#applyTransform();
  }

  #resetTransform() {
    this.#scale = 1;
    this.#tx = 0;
    this.#ty = 0;
    this.#applyTransform();
  }

  #applyTransform() {
    if (!this.#img) return;
    this.#img.style.transform = `translate3d(${this.#tx}px, ${this.#ty}px, 0) scale(${this.#scale})`;
    if (this.#levelLabel) this.#levelLabel.textContent = `${Math.round(this.#scale * 100)}%`;
    if (this.#stage) {
      this.#stage.classList.toggle('image-zoom__stage--zoomed', this.#scale > 1);
    }
  }

  // --- Pointer handling (arrow functions to preserve `this`) ---

  #onPointerDown = (/** @type {PointerEvent} */ e) => {
    if (!this.#stage) return;
    this.#stage.setPointerCapture?.(e.pointerId);
    this.#pointers.set(e.pointerId, e);

    if (this.#pointers.size === 2) {
      // Start pinch
      const [a, b] = Array.from(this.#pointers.values());
      this.#pinchStartDist = distance(a, b);
      this.#pinchStartScale = this.#scale;
      this.#isPanning = false;
    } else if (this.#pointers.size === 1 && this.#scale > 1) {
      this.#isPanning = true;
      this.#panStartX = e.clientX;
      this.#panStartY = e.clientY;
      this.#panStartTx = this.#tx;
      this.#panStartTy = this.#ty;
    }
  };

  #onPointerMove = (/** @type {PointerEvent} */ e) => {
    if (!this.#pointers.has(e.pointerId)) return;
    this.#pointers.set(e.pointerId, e);

    if (this.#pointers.size === 2 && this.#pinchStartDist) {
      const [a, b] = Array.from(this.#pointers.values());
      const dist = distance(a, b);
      const nextScale = this.#pinchStartScale * (dist / this.#pinchStartDist);
      this.#setScale(nextScale);
      return;
    }

    if (this.#isPanning && this.#scale > 1) {
      this.#tx = this.#panStartTx + (e.clientX - this.#panStartX);
      this.#ty = this.#panStartTy + (e.clientY - this.#panStartY);
      this.#applyTransform();
    }
  };

  #onPointerUp = (/** @type {PointerEvent} */ e) => {
    this.#pointers.delete(e.pointerId);
    if (this.#pointers.size < 2) this.#pinchStartDist = null;
    if (this.#pointers.size === 0) this.#isPanning = false;
  };

  #onWheel = (/** @type {WheelEvent} */ e) => {
    e.preventDefault();
    const delta = e.deltaY > 0 ? -ZOOM_STEP / 2 : ZOOM_STEP / 2;
    this.#zoomBy(delta);
  };
}

/**
 * @param {PointerEvent} a
 * @param {PointerEvent} b
 */
function distance(a, b) {
  const dx = a.clientX - b.clientX;
  const dy = a.clientY - b.clientY;
  return Math.hypot(dx, dy);
}

if (!customElements.get('image-zoom')) {
  customElements.define('image-zoom', ImageZoom);
}
